import { Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'pk-broker-form-dropdown',
    templateUrl: './form-dropdown.component.html',
    styleUrls: ['./form-dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            useExisting: FormDropdownComponent,
        },
    ],
    standalone: false
})
export class FormDropdownComponent<T> implements OnInit, ControlValueAccessor {
	public option: T;
	private optionElement: HTMLElement;
	public disabled = false;
	private touched = false;
	private opened = false;
	private optionsTexts: { offsetTop: number; text: string }[];

	@ViewChildren('optionsRef') optionsReferences: QueryList<ElementRef>;
	@ViewChild('formDropdown', { static: false }) formDropdown: any;
	@ViewChild('scrollingArea', { static: false }) scrollingArea: ElementRef;
	@ContentChild('name') public nameTemplate: TemplateRef<{ $implicit: T }>;
	@ContentChild('customDropdownToggleTextContainer') public customDropdownToggleTextContainerTemplate: TemplateRef<{ $implicit: T }>;
	@ContentChild('customDropdownMenuContainer') public customDropdownMenuContainerTemplate: TemplateRef<{ $implicit: T }>;
	@ContentChild('headerTemplate') public headerTemplate: TemplateRef<void>;
	@ContentChild('header') public header: TemplateRef<void>;
	@ContentChild('row') public rowRef: TemplateRef<{ $implicit: T }>;
	@ContentChild('footer') public footer: TemplateRef<void>;
	@Output() selection = new EventEmitter<T>();

	@Input() label: string = null;
	@Input() insideClick = false;
	@Input() tooltipText: string = null;
	@Input() isRequired = false;
	@Input() set isDisabled(disabled: boolean) {
		this.setDisabledState(disabled);
	}
	@Input() nullOption: string = null;
	@Input() nullOptionSelectable = true;
	@Input() templateOnlyMenu = false;
	@Input() options: T[];
	@Input() holdOption = true;
	@Input() initialOption: T = null;
	@Input() scrollAreaHeight: 'medium' | null = null;
	@Input() dropdownClasses = '';
	@Input() dropdownToggleClasses = '';
	@Input() dropdownMenuClasses = '';
	@Input() dropdownMenuRowClasses = '';
	@Input() dropdownItemCssClassKey = '';
	@Input() dropdownToggleTextSpanClasses = '';
	@Input() headerLabelDisplayBlock = false;

	@Input() textKey = 'text';
	/**
	 * If this is defined, ensure that no two options map to the same value.
	 */
	@Input() valueKey: string;

	filterQuery = '';

	/**
	 * Do not put HTML in here, instead use the <ng-template #row let-option></ng-template> and <ng-template #name let-option></ng-template>
	 * angular templates to show HTML in your options, this way the dropdown won't display literal HTML and try to "search" to HTML
	 * when doing the search action to filter the matching elements in textTyped().
	 *
	 * If using the angular templates and you want letter bolding for matched searches just call boldMap() after getting a
	 * ViewChild instance of the FormDropdownComponent object.
	 */
	@Input()
	textMap: (option: T) => string = option => String(typeof option === 'object' && this.textKey in option ? option[this.textKey] : option);

	/**
	 * If this is defined, ensure that no two options map to the same value.
	 */
	@Input()
	valueMap: (option: T) => any = option =>
		typeof option === 'object' && option !== null && this.valueKey in option ? option[this.valueKey] : option;

	onChange = (_option: T) => {
		/* placeholder for interface */
	};
	onTouched = () => {
		/* placeholder for interface */
	};

	ngOnInit() {
		if (this.initialOption !== null) {
			this.option = this.initialOption;
		} else if (this.nullOption && this.nullOptionSelectable) {
			this.option = null;
		} else {
			this.option = this.options[0];
		}

		if (this.holdOption) {
			if (this.initialOption !== null) {
				this.option = this.initialOption;
			} else if (this.nullOption && this.nullOptionSelectable) {
				this.option = null;
			} else {
				this.option = this.hasHeaders ? this.options[this.headers[0]][0] : this.options[0];
			}
		}
	}

	get hasHeaders() {
		return !Array.isArray(this.options);
	}
	get headers() {
		return Object.keys(this.options);
	}

	onSelect(option: T, event: PointerEvent) {
		this.optionElement = event.target as HTMLElement;
		if (!this.disabled) {
			if (this.holdOption) {
				this.markAsTouched();
				this.option = option;
				this.onChange(this.valueMap(option));
			}
			this.selection.emit(this.valueMap(option));
		}
	}

	writeValue(option: T) {
		const targetValue = this.valueMap(option);
		this.option = this.options.find(o => this.valueMap(o) === targetValue) ?? null;
	}

	registerOnChange(onChange: (option: T) => void) {
		this.onChange = onChange;
	}

	registerOnTouched(onTouched: () => void) {
		this.onTouched = onTouched;
	}

	markAsTouched() {
		if (!this.touched) {
			this.onTouched();
			this.touched = true;
		}
	}

	setDisabledState(disabled: boolean) {
		this.disabled = disabled;
	}

	dropdownShown() {
		this.filterQuery = '';
		if (this.optionElement) {
			this.scrollingArea.nativeElement.scrollTop = this.optionElement.offsetTop;
		} else {
			const initialDropdownCheck = setInterval(() => {
				if (this.optionsReferences) {
					this.optionsTexts =
						this.optionsReferences?.map(r => ({ offsetTop: r.nativeElement.offsetTop, text: r.nativeElement.innerText.trim().toLowerCase() })) || [];
					this.scrollToOption(this.option);
					clearInterval(initialDropdownCheck);
				}
			}, 1);
		}
	}

	openChanged(opened: boolean): void {
		this.opened = opened;
	}

	textTyped(event: KeyboardEvent) {
		if (event.key === ' ') {
			event.preventDefault();
		}
		if (event.key === 'Backspace') {
			this.filterQuery = this.filterQuery.substring(0, this.filterQuery.length - 1);
		} else if (event.key.length === 1 && event.key !== ' ') {
			this.filterQuery += event.key.toLowerCase();
		} else {
			return;
		}

		setTimeout(() => {
			if (this.optionsReferences?.first) {
				this.optionsReferences.first.nativeElement.focus();
			} else {
				this.formDropdown._elementRef.nativeElement.firstChild.focus();
			}
		}, 0);
	}

	get dropdownToggleText(): string {
		if (!this.nameTemplate && this.option === null) {
			return this.nullOption;
		}

		if (!this.nameTemplate && this.option !== null && (!this.rowRef || this.templateOnlyMenu)) {
			return this.textMap(this.option);
		}

		return null;
	}

	get visibleOptions(): T[] {
		return this.options.filter(op => {
			const text = this.textMap(op).toLowerCase();
			let pos = 0;
			for (const letter of this.filterQuery) {
				const index = text.indexOf(letter, pos);
				if (index === -1) {
					return false;
				} else {
					pos = index + 1;
				}
			}
			return true;
		});
	}

	public boldMap(str: string): string {
		const text = str.toLowerCase();
		const boldPositions = [];
		let pos = 0;
		for (const letter of this.filterQuery) {
			const index = text.indexOf(letter, pos);
			if (index === -1) {
				return str;
			} else {
				pos = index + 1;
				boldPositions.push(index);
			}
		}

		let result = '';
		let nextBoldPosition = boldPositions.shift();
		for (let i = 0; i < str.length; i++) {
			if (nextBoldPosition === i) {
				result += `<b>${str[i]}</b>`;
				nextBoldPosition = boldPositions.shift();
			} else {
				result += str[i];
			}
		}

		return result;
	}

	private scrollToOption(option: any): void {
		const optionText = this.optionsTexts.find(op => option && op.text === this.textMap(option).toLowerCase());
		if (optionText) {
			this.scrollingArea.nativeElement.scrollTop = optionText.offsetTop;
		}
	}
}
